/**
 * Reine Hilfsfunktionen rund um die Stimmungsverlaufs-Daten aus /stimmungsbarometer/getHistory.
 * Bewusst frei von Vue- und Chart-Abhaengigkeiten, damit alle Ansichten dieselbe Ableitung nutzen.
 *
 * Datenform (ein Eintrag aus season.entries):
 *   { x: "2025-08-02T00:00:00+00:00", y: 6.81, votes: 1313, evnt: null | {
 *       type: 1, text: "1:2", points: 0|1|3|null, md: "1. Spieltag",
 *       image: "https://.../clubs/<id>.png",
 *       opp: "FC Schalke 04", home: true, mdNo: 1   // seit der API-Erweiterung
 *   }}
 */

const MATCHDAY_FROM_TEXT = /^(\d+)\.\s*Spieltag$/;

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dateFormatShort = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' });
const dateFormatShortYear = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

export function formatDate(value) {
  return dateFormat.format(new Date(value));
}

export function formatDateShort(value) {
  return dateFormatShort.format(new Date(value));
}

/** Wie formatDateShort, aber mit zweistelligem Jahr - fuer die Saisonstatistik-Tabelle, wo
 *  mehrere Saisons nebeneinanderstehen und "25.08." allein nicht mehr eindeutig ist. */
export function formatDateShortYear(value) {
  return dateFormatShortYear.format(new Date(value));
}

/** Stimmungswert in deutscher Schreibweise, immer mit zwei Nachkommastellen. */
export function formatScore(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2).replace('.', ',') : '–';
}

/**
 * Kurzform des Saisonnamens fuer Legenden und enge Buttons: "Saison 2025/2026" -> "25/26".
 *
 * Der Name kommt als frei gepflegtes Label aus den Stammdaten, das Format ist also nicht garantiert.
 * Erkannt werden vier- und zweistellige Jahrespaare ("2025/2026" wie "25/26"); passt nichts davon,
 * bleibt das Label unveraendert stehen, statt es zu verstuemmeln.
 */
export function shortSeasonLabel(season) {
  const text = season || '';
  const full = /(\d{2})(\d{2})\s*\/\s*(\d{2})(\d{2})/.exec(text);
  if (full) return `${full[2]}/${full[4]}`;

  const short = /(?<!\d)(\d{2})\s*\/\s*(\d{2})(?!\d)/.exec(text);
  if (short) return `${short[1]}/${short[2]}`;

  return text;
}

/**
 * Numerischer Spieltag eines Events - Voraussetzung fuer die Spieltagsachse im Saisonvergleich.
 * Bevorzugt das API-Feld mdNo; faellt auf den Freitext md zurueck, damit die Ansicht auch gegen
 * einen noch nicht aktualisierten Server funktioniert. Pokalspiele und Spiele ohne gepflegte
 * Rundennummer ("Ligaspiel", "Pokal, Achtelfinale") liefern null und bleiben aus dem Vergleich raus.
 */
export function getMatchdayNumber(evnt) {
  if (!evnt || evnt.type !== 1) return null;
  if (Number.isInteger(evnt.mdNo)) return evnt.mdNo;

  const match = MATCHDAY_FROM_TEXT.exec(evnt.md || '');
  return match ? Number(match[1]) : null;
}

/** Alle Spiel-Eintraege einer Saison (type 1), chronologisch. Sonstige Ereignisse bleiben aussen vor. */
export function getMatchEntries(season) {
  if (!season || !Array.isArray(season.entries)) return [];
  return season.entries
    .filter(entry => entry.evnt && entry.evnt.type === 1)
    .map(entry => ({
      date: entry.x,
      score: entry.y,
      votes: entry.votes,
      evnt: entry.evnt,
      matchday: getMatchdayNumber(entry.evnt)
    }));
}

/**
 * Jeder wievielte Spieltag an der Achse beschriftet wird, und wie viele Tage zwei Beschriftungen
 * mindestens auseinanderliegen muessen.
 *
 * 34 Ligaspieltage plus Pokal waeren an der Achse nicht darstellbar - auf einem schmalen Handy
 * stehen einer Saison rund 300px zur Verfuegung, also gut 8px je Spieltag. Beschriftet wird
 * deshalb nur jeder fuenfte. Der Mindestabstand faengt zusaetzlich die Verdichtung durch
 * englische Wochen ab: Die Spieltage liegen zeitlich nicht gleichmaessig, fuenf Runden koennen
 * sich auf drei Wochen draengen.
 */
export const MATCHDAY_TICK_STEP = 5;
export const MATCHDAY_TICK_MIN_DAYS = 18;

/**
 * Die Spieltage, die an der x-Achse des Verlaufs beschriftet werden - jeweils mit dem Datum,
 * an dem sie liegen, denn die Achse bleibt eine Zeitachse.
 *
 * Pokalspiele tragen keine Rundennummer (getMatchdayNumber liefert null) und bleiben deshalb
 * unbeschriftet; als Punkt sind sie in der Kurve weiterhin da.
 */
export function getMatchdayTicks(season, step = MATCHDAY_TICK_STEP, minDays = MATCHDAY_TICK_MIN_DAYS) {
  const ticks = [];
  let lastDate = null;

  getMatchEntries(season).forEach(entry => {
    const matchday = entry.matchday;
    if (matchday === null) return;
    // Der erste Spieltag ist immer dabei - er markiert den Saisonstart.
    if (matchday !== 1 && matchday % step !== 0) return;

    const date = new Date(entry.date).getTime();
    if (lastDate !== null && (date - lastDate) / 86400000 < minDays) return;

    ticks.push({ matchday, date: entry.date });
    lastDate = date;
  });

  return ticks;
}

/** Alle Nicht-Spiel-Ereignisse einer Saison (z.B. "Sommerpause"). */
export function getOtherEvents(season) {
  if (!season || !Array.isArray(season.entries)) return [];
  return season.entries.filter(entry => entry.evnt && entry.evnt.type !== 1);
}

/**
 * Breite der Trefferzone rund um einen Spieltag, in Pixeln je Seite, zusammen also 16px.
 *
 * Damit liegt die Zone unter dem Mindestmass fuer ein Bedienziel nach WCAG 2.2 (2.5.8, 24px) -
 * vertretbar, weil das Snapping nur den Komfort erhoeht und keinen Inhalt exklusiv erschliesst:
 * Spiel und Ergebnis stehen vollstaendig in der Spieltags-Liste und im Raster. Eine breitere Zone
 * hatte auf dem Desktop dazu gefuehrt, dass ein Spiel praktisch die ganze Woche abdeckt und der
 * Tooltip kaum noch einen reinen Tageswert zeigt.
 */
export const MATCH_SNAP_PX = 8;

/**
 * Waehlt den Eintrag, den der Tooltip fuer einen gehoverten Datenpunkt zeigen soll: den Spieltag
 * in Reichweite, sonst den Punkt selbst.
 *
 * Die Serie hat einen Punkt pro Tag, aber nur rund 40 davon sind Spieltage. Ueber eine volle
 * Saison auf ~900px Chartbreite ist ein Tag etwa 3px breit, auf dem Handy gut 1px - der Spieltag
 * ist also nur in einem 3px-Fenster zu treffen. ApexCharts laesst sich dabei nicht nachjustieren:
 * getNearestValues nimmt immer den x-naechsten Punkt, ohne Toleranz oder Radius. Die Zone muss
 * deshalb hier entstehen.
 *
 * Die Toleranz ist bewusst in Pixeln angegeben und nicht in Tagen: so bleibt die Zone auf jeder
 * Bildschirmbreite und bei jeder Saisonlaenge gleich gross, waehrend eine feste Tagesangabe auf
 * dem Handy zu breit und auf dem Desktop zu schmal ausfiele.
 *
 * @param {Array} entries chronologisch sortierte Saison-Eintraege
 * @param {number} index Position des tatsaechlich gehoverten Punktes
 * @param {number} msPerPixel Umrechnung der Achse, aus Chartbreite und dargestelltem Zeitraum
 */
export function snapToMatch(entries, index, msPerPixel, tolerancePx = MATCH_SNAP_PX) {
  const point = entries && entries[index];
  // Traegt der Punkt selbst ein Ereignis, gibt es nichts zu suchen. Ohne brauchbaren Massstab -
  // etwa vor dem ersten Rendern - bleibt es beim gehoverten Punkt.
  if (!point || point.evnt) return point || null;
  if (!Number.isFinite(msPerPixel) || msPerPixel <= 0) return point;

  const toleranceMs = tolerancePx * msPerPixel;
  const hoveredX = new Date(point.x).getTime();

  let best = point;
  let bestDistance = Infinity;

  // Von der Hover-Position nach beiden Seiten auswaerts laufen und abbrechen, sobald die Zone
  // verlassen ist. Da die Eintraege chronologisch liegen, waechst der Abstand monoton - ueber die
  // ganze Saison zu iterieren waere bei jeder Mausbewegung unnoetige Arbeit.
  [-1, 1].forEach(step => {
    for (let i = index + step; i >= 0 && i < entries.length; i += step) {
      const candidate = entries[i];
      const distance = Math.abs(new Date(candidate.x).getTime() - hoveredX);
      if (distance > toleranceMs) break;
      if (candidate.evnt && distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
  });

  return best;
}

/** Kurzform des Ergebnisses fuer Screenreader und Titel-Attribute. */
export function resultLabel(evnt) {
  if (!evnt || evnt.type !== 1) return 'Ereignis';
  if (evnt.points === 3) return 'Sieg';
  if (evnt.points === 1) return 'Unentschieden';
  if (evnt.points === 0) return 'Niederlage';
  return 'Ohne Ergebnis';
}

/** Gegner mit Heim-/Auswaertskennung, sofern die API sie liefert. */
export function opponentLabel(evnt) {
  if (!evnt) return '';
  if (!evnt.opp) return evnt.md || '';
  return evnt.home === false ? `bei ${evnt.opp}` : evnt.opp;
}

/**
 * Fuenfstufige Stimmungsskala - dieselbe Farbsprache wie der Voting-Slider und das Barometer
 * (siehe ratingData.js und die .rating-*-Klassen in custom.css). Mehrfarbig, aber semantisch
 * geordnet (schlecht -> gut); die Rasteransicht zeigt dazu zwingend eine Skalenlegende.
 */
export const MOOD_SCALE = [
  { max: 2.5, color: '#EA3943', label: 'bis 2,5' },
  { max: 4.5, color: '#EA8C00', label: '2,5–4,5' },
  { max: 6.0, color: '#F3D42F', label: '4,5–6,0' },
  { max: 7.5, color: '#93D900', label: '6,0–7,5' },
  { max: Infinity, color: '#16C784', label: 'ab 7,5' }
];

export function moodColor(score) {
  const num = Number(score);
  if (!Number.isFinite(num)) return '#7a8899';
  return (MOOD_SCALE.find(step => num <= step.max) || MOOD_SCALE[MOOD_SCALE.length - 1]).color;
}

/**
 * Kontextdarstellung einer Saison im Vergleichschart. Bewusst an die Position der Saison in der
 * Gesamtliste gebunden statt an ihren Rang in der gerade sichtbaren Auswahl - so behaelt eine
 * Saison ihre Darstellung, egal welche gerade hervorgehoben ist.
 *
 * Zur Helligkeitsstufe kommt ein Strichmuster: drei Grautoene allein sind bei sich kreuzenden
 * Linien nicht zuverlaessig auseinanderzuhalten, das Muster traegt die Unterscheidung mit.
 */
const CONTEXT_STYLES = [
  { color: '#b9c3d4', dash: 0 },   // 9.28:1
  { color: '#97a3b8', dash: 6 },   // 6.48:1
  { color: '#7b8699', dash: 2 },   // 4.49:1
  { color: '#626d80', dash: 10 }   // 3.16:1
];

/** Zaehlt von der neuesten Saison rueckwaerts, damit jede Saison ihren Stil unabhaengig davon
 *  behaelt, welche gerade hervorgehoben ist. */
function contextStyle(season, allSeasons) {
  const indexFromEnd = allSeasons.length - 1 - allSeasons.findIndex(s => s.season === season.season);
  return CONTEXT_STYLES[Math.min(indexFromEnd, CONTEXT_STYLES.length - 1)];
}

/** So viele Saisons zeigt der Vergleich: die hervorgehobene plus drei zum Einordnen. Mehr Linien
 *  laufen im selben Bild ineinander, und es gibt nur vier unterscheidbare Kontextstufen. */
export const MAX_COMPARISON_SEASONS = 4;

/**
 * Serien fuer den Saisonvergleich: je Saison die Stimmung an jedem Spieltag, auf eine gemeinsame
 * Spieltagsachse normalisiert.
 *
 * Der Wert bei Spieltag N ist der Tagespunkt am Spieltag selbst. Dieser Punkt bildet serverseitig
 * den 14-Tage-Schnitt VOR dem Spiel ab (halboffenes Fenster im StimmungByDayAggregationJob), ist
 * also "die Stimmung, mit der wir in Spieltag N gegangen sind" - fuer alle Saisons gleich definiert
 * und damit fair vergleichbar.
 */
export function buildComparisonSeries(seasons, highlightedSeasonName) {
  // Sichtfenster: die jüngsten Saisons, aber immer einschliesslich der hervorgehobenen.
  const highlightIndex = seasons.findIndex(s => s.season === highlightedSeasonName);
  let start = Math.max(0, seasons.length - MAX_COMPARISON_SEASONS);
  if (highlightIndex >= 0 && highlightIndex < start) start = highlightIndex;
  const visible = seasons.slice(start, start + MAX_COMPARISON_SEASONS);

  const perSeason = visible.map(season => {
    const points = new Map();

    getMatchEntries(season)
      .filter(entry => entry.matchday !== null)
      .forEach(entry => points.set(entry.matchday, entry));

    return { season, points };
  });

  // Gemeinsame Achse: jeder Spieltag, den mindestens eine der sichtbaren Saisons kennt. Jede Serie
  // bekommt zu jedem dieser Spieltage einen Punkt, fehlende Werte als null.
  //
  // Das ist keine Kosmetik, sondern Voraussetzung fuer den Tooltip: ApexCharts adressiert einen
  // Datenpunkt ueber seinen Index, nicht ueber seinen x-Wert. Waren die Serien unterschiedlich
  // lang - eine laufende Saison mit 5 Spieltagen neben abgeschlossenen mit 34 - zeigte derselbe
  // Index in jeder Serie auf einen anderen Spieltag. Zwei Folgen: Der Tooltip nannte einen
  // falschen Spieltag, und er erschien ueberhaupt nur an den Stellen, an denen ApexCharts' interne
  // Pruefung isXoverlap() zufaellig aufging (siehe handleStickyTooltip) - an allen anderen blieb
  // er stumm. Mit gleich langen Serien trifft jeder Index in jeder Serie denselben Spieltag.
  const matchdays = [...new Set(perSeason.flatMap(entry => [...entry.points.keys()]))]
    .sort((a, b) => a - b);

  return perSeason.map(({ season, points }) => {
    const data = matchdays.map(matchday => {
      const entry = points.get(matchday);
      // null laesst ApexCharts die Linie unterbrechen - genau richtig fuer Spieltage, die diese
      // Saison (noch) nicht gespielt hat.
      return entry
        ? { x: matchday, y: entry.score, evnt: entry.evnt, date: entry.date }
        : { x: matchday, y: null };
    });

    const isHighlighted = season.season === highlightedSeasonName;
    const style = contextStyle(season, seasons);

    return {
      name: shortSeasonLabel(season.season),
      seasonName: season.season,
      isHighlighted,
      color: isHighlighted ? '#7cb8ff' : style.color,
      dash: isHighlighted ? 0 : style.dash,
      data
    };
  }).filter(series => series.data.some(point => point.y !== null));
}

/**
 * Die Saison, die im Saisonvergleich standardmaessig als einziger Vergleichswert neben der
 * hervorgehobenen sichtbar ist - alle anderen von buildComparisonSeries gelieferten Saisons
 * bleiben ueber die Legende weiterhin zuschaltbar, starten aber ausgeblendet.
 *
 * Regel: die naechstjuengere Saison (Jahr + 1). Ausnahme die aktuellste Saison selbst - dort gibt
 * es kein juengeres Jahr, deshalb dann die naechstaeltere (Jahr - 1).
 *
 * @returns {string|null} Saisonname der Vergleichssaison, oder null ohne Nachbarsaison (z.B. nur
 *   eine Saison insgesamt, oder highlightedSeasonName nicht in seasons enthalten).
 */
export function defaultComparisonSeason(seasons, highlightedSeasonName) {
  const index = seasons.findIndex(s => s.season === highlightedSeasonName);
  if (index < 0) return null;

  const isNewest = index === seasons.length - 1;
  const neighbor = seasons[isNewest ? index - 1 : index + 1];
  return neighbor ? neighbor.season : null;
}

/**
 * Kennzahlen einer einzelnen Saison fuer die Saisonstatistik-Tabelle: Hoechst-, Tiefst- und
 * Durchschnittswert.
 *
 * Bewusst auf derselben Teilmenge wie buildComparisonSeries berechnet (Ligaspieltage mit
 * bekannter Spieltagsnummer) statt auf season.high/low/avgScore aus der API: Die serverseitigen
 * Werte laufen ueber alle Tage der Saison, auch spielfreie - der Hoechst-/Tiefstwert liesse sich
 * dort oft keinem Spiel zuordnen. Hier ist das garantiert, und die Tabelle bleibt methodisch
 * deckungsgleich mit dem Vergleichschart direkt darueber.
 *
 * Bei mehreren Spielen mit demselben Extremwert gewinnt das chronologisch erste (matches ist
 * chronologisch sortiert, die Vergleiche unten sind bewusst strikt > / <).
 *
 * @returns {Object|null} { seasonName, matchCount, avg, high, low } - high/low sind Eintraege aus
 *   getMatchEntries(). null, wenn die Saison keinen Ligaspieltag mit Spieltagsnummer hat.
 */
export function getSeasonMatchStats(season) {
  const matches = getMatchEntries(season).filter(entry => entry.matchday !== null);
  if (matches.length === 0) return null;

  let high = matches[0];
  let low = matches[0];
  let sum = 0;

  matches.forEach(entry => {
    sum += entry.score;
    if (entry.score > high.score) high = entry;
    if (entry.score < low.score) low = entry;
  });

  return {
    seasonName: season.season,
    matchCount: matches.length,
    avg: sum / matches.length,
    high,
    low
  };
}

/**
 * Zeilen der Saisonstatistik-Tabelle - eine je Saison mit mindestens einem Ligaspieltag - plus die
 * Saisons, die den Tabellen-Rekord fuer Hoechst-, Tiefst- und (besten) Durchschnittswert halten.
 * Anders als buildComparisonSeries absichtlich ohne MAX_COMPARISON_SEASONS-Fenster: das begrenzt
 * nur die im Chart gleichzeitig darstellbaren Linien, waehrend die Tabelle die komplette Historie
 * zeigen soll.
 *
 * Die Rekordhalter bleiben null, wenn nur eine Zeile existiert - unter mehreren Saisons zu
 * vergleichen ist bei einer einzigen nicht aussagekraeftig. Beim Durchschnitt zaehlt bewusst nur
 * der beste, kein schlechtester - die Tabelle soll auf den Blick lenken, keine zweite Bewertung
 * (gut/schlecht) nebenher einfuehren.
 *
 * @param {Array} seasons alle Saisons, unbeschraenkt
 * @returns {{ rows: Array, recordHighSeason: string|null, recordLowSeason: string|null,
 *   bestAvgSeason: string|null }}
 */
export function buildSeasonStatsTable(seasons) {
  const rows = (seasons || []).map(getSeasonMatchStats).filter(Boolean);

  if (rows.length <= 1) {
    return { rows, recordHighSeason: null, recordLowSeason: null, bestAvgSeason: null };
  }

  return {
    rows,
    recordHighSeason: rows.reduce((best, row) => (row.high.score > best.high.score ? row : best)).seasonName,
    recordLowSeason: rows.reduce((worst, row) => (row.low.score < worst.low.score ? row : worst)).seasonName,
    bestAvgSeason: rows.reduce((best, row) => (row.avg > best.avg ? row : best)).seasonName
  };
}

